import { useCallback, useEffect, useRef, useState } from 'react'
import { useWisch } from './bewegung.js'

/**
 * Der Gewerbekreis – ein Auswahlkarussell.
 *
 * Fünf Karten auf einer flachen Ellipse, die mittlere vorn und gross, die
 * äusseren kleiner und blasser. Darunter Pfeile und Punkte.
 *
 * Das Bauteil ist inhaltsfrei: es bekommt eine Liste aus Titel, Satz und
 * optionaler Marke und meldet, welche Karte gewählt wurde. Was die Karten
 * bedeuten, weiss die Seite, die es einsetzt.
 *
 * Bedienbar auf vier Wegen: Karte, Pfeile, Punkte, Pfeiltasten. Der
 * Selbstlauf hält an, sobald Zeiger oder Fokus da sind – wer liest, wird
 * nicht weitergeschoben.
 */

export interface KreisStueck {
  id: string
  titel: string
  text: string
  marke?: string
}

/* Sichtbare Plätze und die Halbachsen der Ellipse. */
const SICHTBAR = 5
const RADIUS_X = 380
const RADIUS_Y = 110

interface Lage {
  x: number
  y: number
  groesse: number
  deckung: number
  ebene: number
}

/**
 * Die Lage einer Karte.
 *
 * Ellipse für den Platz, Abstand zur Mitte für Grösse, Deckkraft und Ebene.
 *
 * Der Versatz wird auf die kurze Strecke um den Kreis gelegt, und zwar an der
 * halben GESAMTZAHL – nicht an der halben Sichtbarkeit. Sonst landen Karten
 * bei grösseren Listen mit Versatz −12 ausserhalb und verschwinden, während
 * sich andere an einer Stelle stapeln.
 */
function lageVon(
  i: number,
  aktiv: number,
  anzahl: number,
  rx: number,
  ry: number,
  spanne: number
): Lage | null {
  const halb = Math.floor(SICHTBAR / 2)
  let v = i - aktiv
  if (v > anzahl / 2) v -= anzahl
  if (v < -anzahl / 2) v += anzahl
  /* Was weiter draussen liegt, wird nicht gezeichnet. Wie viele Nachbarn das
     sind, hängt an der Breite: auf einem schmalen Gerät ist der Radius
     kleiner als eine Karte breit, und fünf Karten darauf sind kein Bogen
     mehr, sondern ein Stapel. */
  if (Math.abs(v) > spanne) return null

  const winkel = (v / SICHTBAR) * Math.PI
  const abstand = Math.abs(v)
  const weiteste = halb + 1

  return {
    x: Math.sin(winkel) * rx,
    /* Der Zuschlag setzt den Bogen herunter: ohne ihn liegt jede Karte über
       der Mitte und unter dem Bogen bleibt eine leere Fläche stehen. */
    y: -Math.cos(winkel) * ry + ry * 0.78,
    /* 0,42 – derselbe Wert, mit dem weiter unten der Radius gedeckelt wird.
       Standen hier zwei verschiedene Zahlen, rechnete der Deckel mit
       kleineren Karten, als tatsächlich gezeichnet wurden. */
    groesse: Math.max(0, 1 - (abstand / weiteste) * 0.42),
    deckung: Math.max(0.3, 1 - (abstand / weiteste) * 0.7),
    ebene: SICHTBAR - abstand
  }
}

export function Gewerbekreis({
  stuecke,
  aktiv,
  aufAktiv,
  selbstlauf = true,
  takt = 4000
}: {
  stuecke: KreisStueck[]
  aktiv: number
  aufAktiv: (i: number) => void
  selbstlauf?: boolean
  takt?: number
}): JSX.Element {
  const [zeiger, setZeiger] = useState(false)
  const [fokus, setFokus] = useState(false)
  const [ruhig, setRuhig] = useState(false)
  const rahmen = useRef<HTMLDivElement>(null)
  const bahn = useRef<HTMLDivElement>(null)
  const anzahl = stuecke.length

  /*
    Radius und Zahl der Nachbarn richten sich nach der Bahn, nicht umgekehrt:
    erst wie viele Nachbarn in die Breite passen, dann wie gross der Radius
    sein darf, damit die äusserste Karte ganz im Bild steht. Mit festem
    Radius lag der Nachbar auf schmalen Geräten gemessen 69 Pixel neben einer
    170 Pixel breiten Karte, also fast vollständig darunter.
  */
  const [mass, setMass] = useState({ rx: RADIUS_X, spanne: 3 })
  useEffect(() => {
    const el = bahn.current
    if (!el) return
    const messen = (): void => {
      const w = el.getBoundingClientRect().width
      const kb = window.innerWidth < 520 ? 112 : window.innerWidth < 760 ? 150 : 168
      const spanne = w >= 700 ? 2 : w >= 480 ? 2 : 1
      const sinF = Math.sin((spanne / SICHTBAR) * Math.PI)
      const skala = 1 - (spanne / (Math.floor(SICHTBAR / 2) + 1)) * 0.42
      setMass({
        rx: Math.max(96, Math.min(RADIUS_X, (w / 2 - (kb * skala) / 2 - 12) / sinF)),
        spanne
      })
    }
    messen()
    const beobachter = new ResizeObserver(messen)
    beobachter.observe(el)
    return () => beobachter.disconnect()
  }, [])
  const { rx, spanne } = mass
  const ry = rx * (RADIUS_Y / RADIUS_X)

  useEffect(() => {
    setRuhig(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [])

  const hin = useCallback(
    (i: number) => {
      aufAktiv(((i % anzahl) + anzahl) % anzahl)
    },
    [anzahl, aufAktiv]
  )

  const vor = useCallback(() => hin(aktiv + 1), [aktiv, hin])
  const zurueck = useCallback(() => hin(aktiv - 1), [aktiv, hin])

  /* Wischen – dieselbe Geste wie in der Branchenschau, und aus demselben
     Grund über eine Referenz: mehrere Schritte im selben Ereignis müssen
     aufeinander aufbauen. */
  const stand = useRef(aktiv)
  useEffect(() => {
    stand.current = aktiv
  }, [aktiv])
  useWisch(
    bahn,
    useCallback(
      (richtung: 1 | -1) => {
        stand.current = ((stand.current + richtung) % anzahl + anzahl) % anzahl
        aufAktiv(stand.current)
      },
      [anzahl, aufAktiv]
    )
  )

  /* Der Selbstlauf hält an, sobald jemand da ist. */
  useEffect(() => {
    if (!selbstlauf || zeiger || fokus || ruhig) return
    const uhr = setInterval(vor, takt)
    return () => clearInterval(uhr)
  }, [selbstlauf, zeiger, fokus, ruhig, takt, vor])

  return (
    <div
      ref={rahmen}
      className="rp-gkr"
      tabIndex={0}
      role="listbox"
      aria-label="Punkt wählen"
      onMouseEnter={() => setZeiger(true)}
      onMouseLeave={() => setZeiger(false)}
      onFocus={() => setFokus(true)}
      onBlur={() => setFokus(false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          zurueck()
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault()
          vor()
        }
      }}
    >
      <div className="rp-gkr-bahn" ref={bahn}>
        {stuecke.map((s, i) => {
          const l = lageVon(i, aktiv, anzahl, rx, ry, spanne)
          if (!l) return null
          const ist = i === aktiv
          return (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={ist}
              className="rp-gkr-karte"
              data-aktiv={ist ? 'ja' : undefined}
              onClick={() => hin(i)}
              style={{
                transform: `translate(-50%, -50%) translate(${l.x.toFixed(1)}px, ${l.y.toFixed(
                  1
                )}px) scale(${l.groesse.toFixed(3)})`,
                opacity: l.deckung,
                zIndex: l.ebene
              }}
            >
              {s.marke && <span className="rp-gkr-marke">{s.marke}</span>}
              <span className="rp-gkr-wort">
                <strong>{s.titel}</strong>
                <small>{s.text}</small>
              </span>
            </button>
          )
        })}

      </div>

      <div className="rp-gkr-leiste">
        <button
          type="button"
          className="rp-gkr-pfeil"
          aria-label="Vorheriger Punkt"
          onClick={zurueck}
        >
          <Pfeil richtung="links" />
        </button>

        <div className="rp-gkr-punkte">
          {stuecke.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className="rp-gkr-punkt"
              data-aktiv={i === aktiv ? 'ja' : undefined}
              aria-label={s.titel}
              onClick={() => hin(i)}
            />
          ))}
        </div>

        <button type="button" className="rp-gkr-pfeil" aria-label="Nächster Punkt" onClick={vor}>
          <Pfeil richtung="rechts" />
        </button>
      </div>
    </div>
  )
}

function Pfeil({ richtung }: { richtung: 'links' | 'rechts' }): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {richtung === 'links' ? <path d="M15 18 9 12l6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  )
}
